import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import {
	useCreateTrackingMutation,
	useLazyGetOnboardingIcpQuery,
} from "@/pages/Private/redux/onboardingIcp/onboardingIcp.api";
import {
	usePreviewCompanyEnrichMutation,
	useResolvePositionsMutation,
} from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import {
	CompanyEnrichPreviewPerson,
	CreateCompanyEnrich,
} from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

import { resolveIcpCountries } from "./resolveIcpCountries";
import { resolveIcpPositions } from "./resolveIcpPositions";
import { TrackFilterValues, fromIcp } from "./trackFilters";
import { TrackRelaxation, buildTrackSearchLadder, runTrackSearchLadder } from "./trackRelaxation";
import { selectionAfterSearch, trackablePeople } from "./trackSelection";
import { seedTrackFilters } from "./trackSeedFilters";
import { TRACK_LOAD_TIMEOUT_MS, withBudget } from "./trackLoadBudget";
import { useTrackFilterLookups } from "./useTrackFilterLookups";

/** Where the step's starting filters come from, and what may still be widened. */
interface TrackSeed {
	filters: TrackFilterValues;
	/** The ICP personas the widening falls back to; empty for picked criteria. */
	personas: string[];
}

/** One page of suggestions is what the step offers, so the cap is that page — see TRACK_PAGE_SIZE. */
export const TRACK_MAX = 10;
/** The tracker list only produces useful signals with a handful of accounts in it. */
export const TRACK_MIN = 5;
/** Rows per request; "find more people" appends one more page of this size. */
export const TRACK_PAGE_SIZE = 10;

const TOAST_MS = 2800;

export interface TrackPicker {
	people: CompanyEnrichPreviewPerson[];
	visiblePeople: CompanyEnrichPreviewPerson[];
	selectedIds: Set<number>;
	manualUrls: string[];
	totalSelected: number;
	hasMore: boolean;
	isLoading: boolean;
	isSearching: boolean;
	isLoadingMore: boolean;
	isSubmitting: boolean;
	/** The suggestions could not be built — the empty table is a failure, not an answer. */
	loadFailed: boolean;
	/** Builds the suggestions again after a failure. */
	retryLoad: () => Promise<void>;
	toast: string | null;
	/** The ICP-derived starting filters the pane opens with. */
	initialFilters: TrackFilterValues | null;
	/** Set when the ICP search found nobody and had to be widened to fill the table. */
	relaxation: TrackRelaxation | null;
	toggle: (id: number) => void;
	remove: (id: number) => void;
	/** Puts a person found by the "add a person" search into the table, already ticked. */
	addPerson: (person: CompanyEnrichPreviewPerson) => void;
	setManualUrls: (urls: string[]) => void;
	showMore: () => Promise<void>;
	search: (request: CreateCompanyEnrich) => Promise<void>;
	submit: () => Promise<boolean>;
}

/**
 * The Track step's picking state. The suggestions are not a bespoke endpoint:
 * the confirmed ICP (`GET /onboarding-icp`) is turned into a people search that
 * runs through `POST /company-enrich/people/search/preview`, the same endpoint
 * the Lead DB form uses — so the pane edits the very filters that were used.
 */
export const useTrackPicker = (): TrackPicker => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`onboarding.track.${key}`);

	const [fetchIcp] = useLazyGetOnboardingIcpQuery();
	const [previewPeople] = usePreviewCompanyEnrichMutation();
	const [resolvePositions] = useResolvePositionsMutation();

	const [createTracking] = useCreateTrackingMutation();
	const { fetchCountries, fetchAllCountries } = useTrackFilterLookups();
	const { user } = useAppSelector(authSelector);

	// Always paged: the preview endpoint caps at a single page of 10, so asking
	// for a page routes the request to the real people search — which is what
	// makes "find more people" and the total both mean something.
	const runSearch = (request: CreateCompanyEnrich, page: number) =>
		previewPeople({ ...request, page, pageSize: TRACK_PAGE_SIZE }).unwrap();

	const [people, setPeople] = useState<CompanyEnrichPreviewPerson[]>([]);
	const [initialFilters, setInitialFilters] = useState<TrackFilterValues | null>(null);
	const [relaxation, setRelaxation] = useState<TrackRelaxation | null>(null);
	const [totalItems, setTotalItems] = useState(0);
	// Rows as the server counted them, before the company-less ones are dropped — comparing the
	// filtered table against `totalItems` would offer "find more" on a search that is exhausted.
	const [fetchedCount, setFetchedCount] = useState(0);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	// The search behind the current table, replayed with a higher page by "find more".
	const requestRef = useRef<CreateCompanyEnrich>({} as CreateCompanyEnrich);
	const pageRef = useRef(1);
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [manualUrls, setManualUrlsState] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const [isSearching, setIsSearching] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [toast, setToast] = useState<string | null>(null);
	const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const bootedRef = useRef(false);

	const totalSelected = selectedIds.size + manualUrls.length;

	const showToast = (message: string) => {
		setToast(message);

		if (toastTimer.current) {
			clearTimeout(toastTimer.current);
		}

		toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
	};

	/** The ICP turned into a people search — the fallback when nothing was picked. */
	const seedFromIcp = async (): Promise<TrackSeed> => {
		const icp = await fetchIcp().unwrap();
		const [countries, titles] = await Promise.all([
			resolveIcpCountries(icp.location, fetchCountries),
			// The ICP personas are the user's own words ("CEOs"), which the
			// people index does not match literally — resolve them to real
			// job titles first (PREC-3719).
			resolveIcpPositions(icp.personas, resolvePositions),
		]);

		return { filters: fromIcp({ ...icp, personas: titles }, countries), personas: icp.personas };
	};

	/**
	 * The criteria of the lead-database (or lookalike) list the previous step
	 * built, used as they are — the user already told us who to look for there,
	 * so asking the model to guess it again would only disagree with them.
	 */
	const seedFromPickedList = async (): Promise<TrackSeed | null> => {
		const filters = await seedTrackFilters(user?.id ?? 0, fetchAllCountries);

		return filters ? { filters, personas: [] } : null;
	};

	const loadSuggestions = async () => {
		setIsLoading(true);
		setLoadFailed(false);

		try {
			const { step, items, total } = await withBudget(
				(async () => {
					const { filters, personas } = (await seedFromPickedList()) ?? (await seedFromIcp());

					// Published before the ladder runs so a build that fails or times out still
					// shows the criteria it tried, rather than dropping the user on an empty
					// rail. Nothing is on screen yet, so this cannot swap under them: the step
					// waits for the whole build (see `OnboardingStepLoading`).
					setInitialFilters(filters);

					// Criteria that match nobody literally still have people behind them —
					// the ladder widens until the table fills (PREC-3719).
					const ladder = buildTrackSearchLadder(filters, personas);
					const {
						step: reached,
						items: found,
						totalItems: foundTotal,
					} = await runTrackSearchLadder(ladder, (request) => runSearch(request, 1));

					return { step: reached, items: found, total: foundTotal };
				})(),
				TRACK_LOAD_TIMEOUT_MS
			);

			// The pane opens on — and "find more people" pages — the search that
			// actually produced the table, not the one the ICP started from.
			setInitialFilters(step.filters);
			setRelaxation(step.relaxation);
			requestRef.current = step.request;

			const visible = trackablePeople(items);

			setPeople(visible);
			setTotalItems(total);
			setFetchedCount(items.length);
			setSelectedIds(
				selectionAfterSearch({
					people: visible,
					previousSelected: new Set(),
					previousSeen: new Set(),
					reserved: 0,
					max: TRACK_MAX,
				})
			);
		} catch (err) {
			console.error(err);
			// Both said: the toast carries it while it is on screen, the table keeps
			// saying it afterwards — otherwise an empty table reads as "nobody matched".
			setLoadFailed(true);
			showToast(ts("loadFailed"));
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (bootedRef.current) {
			return;
		}

		bootedRef.current = true;

		loadSuggestions();
	}, []);

	const toggle = (id: number) => {
		// Checked out here rather than inside the updater: an updater that toasts is a side effect
		// React is free to run twice.
		if (!selectedIds.has(id) && totalSelected >= TRACK_MAX) {
			showToast(t("onboarding.track.maxToast", { max: TRACK_MAX }));

			return;
		}

		setSelectedIds((prev) => {
			const next = new Set(prev);

			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}

			return next;
		});
	};

	const remove = (id: number) => {
		setPeople((prev) => prev.filter((person) => person.id !== id));
		setSelectedIds((prev) => {
			const next = new Set(prev);

			next.delete(id);

			return next;
		});
	};

	/**
	 * A person the user searched for is added the way a suggested one arrives: as a row at the top
	 * of the table, already ticked. Adding by enrich id rather than as a manual URL is what gives
	 * the row its preview (company, position, photo) and saves the backend a LinkedIn resolve.
	 */
	const addPerson = (person: CompanyEnrichPreviewPerson) => {
		if (people.some((known) => known.id === person.id)) {
			// Already listed — tick it rather than adding a duplicate row.
			if (!selectedIds.has(person.id)) {
				toggle(person.id);
			}

			return;
		}

		if (totalSelected >= TRACK_MAX) {
			showToast(t("onboarding.track.maxToast", { max: TRACK_MAX }));

			return;
		}

		setPeople((prev) => [person, ...prev]);
		setSelectedIds((prev) => new Set(prev).add(person.id));
	};

	const setManualUrls = (urls: string[]) => {
		if (urls.length > manualUrls.length && totalSelected >= TRACK_MAX) {
			showToast(t("onboarding.track.maxToast", { max: TRACK_MAX }));

			return;
		}

		setManualUrlsState(urls);
	};

	const search = async (request: CreateCompanyEnrich) => {
		setIsSearching(true);
		// The note explains what we did to the ICP search — it says nothing about
		// the filters the user just typed themselves.
		setRelaxation(null);
		requestRef.current = request;
		pageRef.current = 1;

		try {
			const { items, totalItems: total } = await runSearch(request, 1);
			const visible = trackablePeople(items);
			const previousSeen = new Set(people.map((person) => person.id));

			setLoadFailed(false);
			setPeople(visible);
			setTotalItems(total);
			setFetchedCount(items.length);
			setSelectedIds((prev) =>
				selectionAfterSearch({
					people: visible,
					previousSelected: prev,
					previousSeen,
					reserved: manualUrls.length,
					max: TRACK_MAX,
				})
			);
		} catch (err) {
			console.error(err);
			setLoadFailed(true);
			showToast(ts("loadFailed"));
		} finally {
			setIsSearching(false);
		}
	};

	/** Re-runs the current search one page further and appends what comes back. */
	const showMore = async () => {
		if (isLoadingMore) {
			return;
		}

		setIsLoadingMore(true);

		const nextPage = pageRef.current + 1;

		try {
			const { items } = await runSearch(requestRef.current, nextPage);

			pageRef.current = nextPage;

			setFetchedCount((prev) => prev + items.length);
			setPeople((prev) => {
				const known = new Set(prev.map((person) => person.id));
				const fresh = trackablePeople(items).filter((person) => !known.has(person.id));

				return [...prev, ...fresh];
			});
		} catch (err) {
			console.error(err);
			showToast(ts("loadFailed"));
		} finally {
			setIsLoadingMore(false);
		}
	};

	const submit = async (): Promise<boolean> => {
		if (totalSelected < TRACK_MIN || isSubmitting) {
			return false;
		}

		setIsSubmitting(true);

		try {
			await createTracking({
				personEnrichIds: [...selectedIds],
				manualLinkedinUrls: manualUrls,
			}).unwrap();

			return true;
		} catch (err) {
			console.error(err);
			showToast(ts("createFailed"));

			return false;
		} finally {
			setIsSubmitting(false);
		}
	};

	return {
		people,
		visiblePeople: people,
		selectedIds,
		manualUrls,
		totalSelected,
		hasMore: fetchedCount < totalItems,
		isLoading,
		isSearching,
		isSubmitting,
		loadFailed,
		retryLoad: loadSuggestions,
		toast,
		initialFilters,
		relaxation,
		toggle,
		remove,
		addPerson,
		setManualUrls,
		isLoadingMore,
		showMore,
		search,
		submit,
	};
};
